import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import {
  ConversationFilterNames,
  conversationFilterData,
} from "@redotech/effect-api-shared/schema/conversation-filters";
import {
  ARRAY_OPERATORS,
  DATE_COMPARISON_OPERATORS,
  NULL_CHECK_OPERATORS,
  STATUS_OPERATORS,
  STRING_PATTERN_OPERATORS,
  type ArrayOperator,
  type DateComparisonOperator,
  type EqualityOperator,
  type NullCheckOperator,
  type StringPatternOperator,
} from "@redotech/effect-api-shared/schema/query-options";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import type { DateFilterData } from "@redotech/redo-model/views/advanced-filters/date-filter";
import { DateFilterOperator } from "@redotech/redo-model/views/advanced-filters/date-filter";
import type { ExistsFilterData } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import type { Ref, SimpleComparison } from "@tanstack/db";
import { and, eq, ilike, inArray, isNull, not, or } from "@tanstack/db";

type TanstackExpression = ReturnType<typeof eq>;

function isArrayStringFilter(
  filter: AdvancedFilterData,
): filter is ArrayStringFilterData {
  return filter.type === AdvancedFilterType.ARRAY_TO_STRING;
}

function isExistsFilter(
  filter: AdvancedFilterData,
): filter is ExistsFilterData {
  return filter.type === AdvancedFilterType.EXISTS;
}

/**
 * Converts a single AdvancedFilterData to a TanStack DB expression.
 *
 * Note: Only handles filters that can be applied client-side to TanStack DB.
 * Complex filters (date ranges, tags requiring joins, etc.) return null
 * and are handled server-side via OpenSearch.
 */
export function advancedFilterToTanstackExpression(
  filter: AdvancedFilterData,
  conversationRef: Ref<ConversationListItemResponse>,
): TanstackExpression | null {
  switch (filter.name) {
    case ConversationFilterNames.STATUS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      const fieldRef = conversationRef.status;
      const values = filter.value ?? [];

      if (values.length === 0) {
        return null;
      }

      switch (filter.operator) {
        case ArrayStringFilterOperator.ANY_OF:
          return values.length === 1
            ? eq(fieldRef, values[0])
            : inArray(fieldRef, values);
        case ArrayStringFilterOperator.NONE_OF:
          return values.length === 1
            ? not(eq(fieldRef, values[0]))
            : not(inArray(fieldRef, values));
        default:
          return null;
      }
    }

    case ConversationFilterNames.READ_AT: {
      if (!isExistsFilter(filter)) {
        return null;
      }
      const fieldRef = conversationRef.readAt;

      switch (filter.operator) {
        case ExistsFilterOperator.EXISTS:
          return not(isNull(fieldRef));
        case ExistsFilterOperator.NOT_EXISTS:
          return isNull(fieldRef);
        default:
          return null;
      }
    }

    case ConversationFilterNames.WORDS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      const summaryRef = conversationRef.summary;
      const values = filter.value ?? [];

      if (values.length === 0 || !values[0]) {
        return null;
      }

      const searchTerm = values[0];

      switch (filter.operator) {
        case ArrayStringFilterOperator.CONTAINS:
          return ilike(summaryRef as Ref<string>, `%${searchTerm}%`);
        case ArrayStringFilterOperator.NOT_CONTAINS:
          return not(ilike(summaryRef as Ref<string>, `%${searchTerm}%`));
        default:
          return null;
      }
    }

    case ConversationFilterNames.ASSIGNEES:
    case ConversationFilterNames.CHANNELS:
    case ConversationFilterNames.CONVERSATION_TAGS:
    case ConversationFilterNames.CUSTOMER_TAGS:
    case ConversationFilterNames.MENTIONS:
    case ConversationFilterNames.CREATED_AT:
    case ConversationFilterNames.CLOSED_AT:
    case ConversationFilterNames.LAST_RESPONSE_AT:
    case ConversationFilterNames.LAST_CUSTOMER_MESSAGE_AT:
    case ConversationFilterNames.LAST_MERCHANT_MESSAGE_AT:
      return null;

    default:
      return null;
  }
}

/**
 * Converts a FilterGroup to a TanStack DB expression, handling AND/OR logic.
 *
 * Only filters that can be applied client-side will be included.
 * Server-side-only filters are skipped (they're handled via OpenSearch).
 */
export function filterGroupToTanstackExpression(
  group: FilterGroup,
  conversationRef: Ref<ConversationListItemResponse>,
): TanstackExpression | null {
  const expressions: TanstackExpression[] = [];

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      // Recursively process nested groups
      const nestedExpr = filterGroupToTanstackExpression(
        clause,
        conversationRef,
      );
      if (nestedExpr !== null) {
        expressions.push(nestedExpr);
      }
    } else {
      // Process leaf filter
      const expr = advancedFilterToTanstackExpression(clause, conversationRef);
      if (expr !== null) {
        expressions.push(expr);
      }
    }
  }

  if (expressions.length === 0) {
    return null;
  }

  if (expressions.length === 1) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return expressions[0];
  }

  // Combine expressions with AND or OR based on group operator
  if (group.operator === FilterOperator.AND) {
    return and(expressions[0], expressions[1], ...expressions.slice(2));
  } else {
    return or(expressions[0], expressions[1], ...expressions.slice(2));
  }
}

/**
 * Converts a flat array of AdvancedFilterData to a TanStack DB expression.
 * All filters are combined with AND logic.
 *
 * This is a convenience function for when you don't have a FilterGroup.
 */
export function filtersToTanstackExpression(
  filters: AdvancedFilterData[],
  conversationRef: Ref<ConversationListItemResponse>,
): TanstackExpression | null {
  const expressions: TanstackExpression[] = [];

  for (const filter of filters) {
    const expr = advancedFilterToTanstackExpression(filter, conversationRef);
    if (expr !== null) {
      expressions.push(expr);
    }
  }

  if (expressions.length === 0) {
    return null;
  }

  if (expressions.length === 1) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return expressions[0];
  }

  return and(expressions[0], expressions[1], ...expressions.slice(2));
}

/**
 * TanStack filter with strongly-typed operators.
 * Discriminated union based on operator type for type-safe value access.
 */
interface TanstackArrayFilter {
  field: (string | number)[];
  operator: ArrayOperator;
  value?: string[];
}

interface TanstackEqualityFilter {
  field: (string | number)[];
  operator: EqualityOperator;
  value?: string;
}

interface TanstackStringPatternFilter {
  field: (string | number)[];
  operator: StringPatternOperator;
  value?: string;
}

interface TanstackNullCheckFilter {
  field: (string | number)[];
  operator: NullCheckOperator;
  value?: undefined;
}

interface TanstackDateComparisonFilter {
  field: (string | number)[];
  operator: DateComparisonOperator;
  value?: string | [string, string];
}

type TanstackFilter =
  | TanstackArrayFilter
  | TanstackEqualityFilter
  | TanstackStringPatternFilter
  | TanstackNullCheckFilter
  | TanstackDateComparisonFilter;

const VALID_OPERATORS = new Set<string>([
  ...STATUS_OPERATORS,
  ...ARRAY_OPERATORS,
  ...STRING_PATTERN_OPERATORS,
  ...NULL_CHECK_OPERATORS,
  ...DATE_COMPARISON_OPERATORS,
]);

function isTanstackFilter(
  input: SimpleComparison,
): input is SimpleComparison & TanstackFilter {
  return VALID_OPERATORS.has(input.operator);
}

/**
 * Maps TanStack field paths to ConversationFilterNames
 */
const FIELD_TO_FILTER_NAME: Record<string, ConversationFilterNames> = {
  status: ConversationFilterNames.STATUS,
  assigneeIds: ConversationFilterNames.ASSIGNEES,
  channels: ConversationFilterNames.CHANNELS,
  tags: ConversationFilterNames.CONVERSATION_TAGS,
  customerTags: ConversationFilterNames.CUSTOMER_TAGS,
  mentions: ConversationFilterNames.MENTIONS,
  readAt: ConversationFilterNames.READ_AT,
  summary: ConversationFilterNames.WORDS,
  createdAt: ConversationFilterNames.CREATED_AT,
  closedAt: ConversationFilterNames.CLOSED_AT,
  lastResponseAt: ConversationFilterNames.LAST_RESPONSE_AT,
  lastCustomerMessageAt: ConversationFilterNames.LAST_CUSTOMER_MESSAGE_AT,
  lastMerchantMessageAt: ConversationFilterNames.LAST_MERCHANT_MESSAGE_AT,
};

/**
 * Maps TanStack operators to ArrayStringFilterOperator
 */
function mapArrayOperator(
  tanstackOp: TanstackFilter["operator"],
): ArrayStringFilterOperator | null {
  switch (tanstackOp) {
    case "eq":
    case "in":
      return ArrayStringFilterOperator.ANY_OF;
    case "not_in":
      return ArrayStringFilterOperator.NONE_OF;
    case "ilike":
    case "like":
      return ArrayStringFilterOperator.CONTAINS;
    default:
      return null;
  }
}

/**
 * Maps TanStack operators to DateFilterOperator
 */
function mapDateOperator(
  tanstackOp: TanstackFilter["operator"],
): DateFilterOperator | null {
  switch (tanstackOp) {
    case "gte":
      return DateFilterOperator.AFTER;
    case "lte":
      return DateFilterOperator.BEFORE;
    case "between":
      return DateFilterOperator.WITHIN;
    default:
      return null;
  }
}

/**
 * Converts a single TanStack filter to AdvancedFilterData.
 * Returns null if the filter cannot be converted.
 */
function tanstackFilterToAdvancedFilter(
  filter: TanstackFilter,
): AdvancedFilterData | null {
  const fieldName = filter.field[0];
  if (typeof fieldName !== "string") {
    return null;
  }

  const filterName = FIELD_TO_FILTER_NAME[fieldName];
  if (!filterName) {
    return null;
  }

  const defaultData = conversationFilterData[filterName];

  switch (defaultData.type) {
    case AdvancedFilterType.ARRAY_TO_STRING: {
      const operator = mapArrayOperator(filter.operator);
      if (!operator) {
        return null;
      }

      let value: string[] = [];
      if (filter.value !== undefined) {
        if (Array.isArray(filter.value)) {
          value = filter.value.filter(
            (v): v is string => typeof v === "string",
          );
        } else if (typeof filter.value === "string") {
          // Handle ilike pattern: strip % wildcards
          const cleanValue = filter.value.replace(/%/g, "");
          value = [cleanValue];
        }
      }

      return {
        name: filterName,
        type: AdvancedFilterType.ARRAY_TO_STRING,
        operator,
        value,
      } as ArrayStringFilterData;
    }

    case AdvancedFilterType.EXISTS: {
      // TanStack uses "isNull" for NOT_EXISTS and "isNotNull" for EXISTS
      let operator: ExistsFilterOperator;
      let value: boolean;

      if (filter.operator === "isNull") {
        operator = ExistsFilterOperator.EXISTS;
        value = false; // isNull means field does NOT exist (unread)
      } else if (filter.operator === "not_isNull") {
        operator = ExistsFilterOperator.EXISTS;
        value = true; // isNotNull means field EXISTS (read)
      } else {
        return null;
      }

      return {
        name: filterName,
        type: AdvancedFilterType.EXISTS,
        operator,
        value,
      } as ExistsFilterData;
    }

    case AdvancedFilterType.DATE: {
      const operator = mapDateOperator(filter.operator);
      if (!operator) {
        return null;
      }

      return {
        name: filterName,
        type: AdvancedFilterType.DATE,
        operator,
        value: filter.value as string | null,
      } as DateFilterData;
    }

    default:
      return null;
  }
}

/**
 * Converts TanStack loadSubsetOptions filters to a FilterGroup.
 * This allows the collection's queryFn to convert TanStack query predicates
 * back to the AdvancedFilterData format for sending to the backend.
 */
export function tanstackFiltersToFilterGroup(
  filters: readonly SimpleComparison[],
): FilterGroup {
  const clauses: AdvancedFilterData[] = [];

  for (const filter of filters) {
    if (!isTanstackFilter(filter)) {
      continue;
    }
    const advancedFilter = tanstackFilterToAdvancedFilter(filter);
    if (advancedFilter) {
      clauses.push(advancedFilter);
    }
  }

  return { operator: FilterOperator.AND, clauses };
}
